'use client'

import React, { useRef, useCallback, useMemo, useState, useEffect } from 'react'
import { useTranslations, useLocale } from 'next-intl'

import trendingDestinationsData from '@/src/constants/trendingDestinations.json'
import { domainCountry } from '@/src/constants/domain'

import { useIsHeya } from '@/src/hooks/useThemeKey'

import { TrendingDestinations } from './trendingDestinations'
import { useHomeSearchPrefill } from '@/src/utils/navigation/useHomeSearchPrefill'

interface TrendingDestinationItem {
    id: string
    name_en: string
    name_ar?: string
    imageUrl: string
    href_en: string
    href_ar?: string
}

interface TrendingDestinationsData {
    title: string
    title_ar?: string
    destinations: TrendingDestinationItem[]
    destinations_heya: TrendingDestinationItem[]
    flightdestinations?: Record<string, TrendingDestinationItem[]>
}

interface TrendingDestinationsWrapperProps {
    showFlightsComponents?: boolean
}

export const TrendingDestinationsWrapper: React.FC<TrendingDestinationsWrapperProps> = ({ showFlightsComponents }) => {
    const t = useTranslations('homePage.headings')
    const locale = useLocale()
    const isRTL = locale === 'ar'
    const isArabic = locale === 'ar'
    const isHeya = useIsHeya()
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const [isMounted, setIsMounted] = useState(false)
    const { handlePrefillFromHref } = useHomeSearchPrefill()

    // Ensure we only use domainCountry after client-side hydration to avoid hydration mismatch
    useEffect(() => {
        setIsMounted(true)
    }, [])

    // Load appropriate data and transform it
    const data = useMemo(() => {
        const allData = trendingDestinationsData as TrendingDestinationsData

        // Only use flight destinations after mount to ensure consistent hydration
        if (showFlightsComponents && allData.flightdestinations && isMounted) {
            const flightsByRegion = allData.flightdestinations
            const flightsList = flightsByRegion[domainCountry] || flightsByRegion['global'] || []

            if (flightsList.length > 0) {
                return {
                    title: isArabic ? allData.title_ar || allData.title : allData.title,
                    destinations: flightsList.map(dest => ({
                        id: dest.id,
                        name: isArabic ? dest.name_ar || dest.name_en : dest.name_en,
                        imageUrl: dest.imageUrl,
                        href: isArabic ? dest.href_ar || dest.href_en : dest.href_en
                    }))
                }
            }
        }

        const destinations = isHeya ? allData.destinations_heya : allData.destinations

        return {
            title: isArabic ? allData.title_ar || allData.title : allData.title,
            destinations: destinations.map(dest => ({
                ...dest,
                name: isArabic ? dest.name_ar || dest.name_en : dest.name_en,
                href: isArabic ? dest.href_ar || dest.href_en : dest.href_en
            }))
        }
    }, [showFlightsComponents, isMounted, isArabic, isHeya])

    if (!data || !data.destinations || data.destinations.length === 0) {
        return null
    }

    const scroll = useCallback(
        (direction: 'left' | 'right') => {
            if (!scrollContainerRef.current) return

            const container = scrollContainerRef.current
            const containerWidth = container.offsetWidth
            const gap = 24
            const totalGaps = 3 * gap
            const cardWidth = (containerWidth - totalGaps) / 4
            const scrollAmount = cardWidth + gap
            const scrollPosition = direction === 'left' ? -scrollAmount : scrollAmount

            container.scrollBy({
                left: isRTL ? -scrollPosition : scrollPosition,
                behavior: 'smooth'
            })
        },
        [isRTL]
    )

    return (
        <TrendingDestinations
            title={t('trendingDestinations')}
            destinations={data.destinations}
            isRTL={isRTL}
            scrollContainerRef={scrollContainerRef}
            onScroll={scroll}
            ctaLabel={isHeya ? t('knowMore') : t('bookNow')}
            onDestinationClick={handlePrefillFromHref}
        />
    )
}

export default TrendingDestinationsWrapper
